import React, { useCallback } from 'react'
import { flatMap } from 'lodash'
import { useRouter } from '@src/routes'
import { useIntl, FormattedMessage } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { Typography } from '@mui/material'
import { Facet } from '@src/types/graphql-types'
import messages from '@utils/messages'
import { isTermlessSearch } from '@utils/termlessSearch'
import { buildFacetEventPayload } from '@utils/analytics/facets'
import { sendEvent } from '@utils/analytics/coreAnalytics'
import {
  useSearchQuery,
  FacetChangeAction,
} from '../../../pages/[country]/[language]/search/[searchTerm]'
import HandleMarkup from '@src/components/HandleMarkup'
import { formatFacetName } from '../Facets/FormattedFacetMessage'
import { getValidSearchQuery, SearchFocusType } from '@src/utils/searchUtils'
import { useResponsive } from '@src/utils/useResponsiveSizes'
import SelectionChipSrpRedesign from '@src/components/SelectionChipSrpRedesign'

const useStyles = makeStyles()((theme) => ({
  facetMarkup: {
    marginRight: theme.spacing(1),
    '& sub, & sup': {
      verticalAlign: 'baseline',
      position: 'relative',
      top: '0.4em',
    },
    '& sup': {
      top: '-0.4em',
    },
  },
  searchTerm: {
    marginRight: theme.spacing(1),
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: theme.spacing(-1),
  },
  chipWrapper: {
    padding: theme.spacing(1),
    '& sub': {
      top: '0.2em',
    },
  },
  keywordChipWrapper: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    maxWidth: '100%',
    padding: theme.spacing(1),
  },
  facetsMenuWrapper: {
    display: 'flex',
    padding: theme.spacing(3),
    background: theme.palette.background.grey,
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: 5,
    marginBottom: theme.spacing(5),
    flexDirection: 'column',
  },
  facetsMenuLabel: {
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(4),
    fontWeight: theme.typography.fontWeightMedium,
    paddingBottom: theme.spacing(2),
  },
}))

interface ChipLabelProps {
  facetLabel: string
  facetKey: string
  facets: Facet[] | undefined
}

const ChipLabel: React.FC<ChipLabelProps> = ({
  facetLabel,
  facetKey,
  facets,
}) => {
  const router = useRouter()
  const { classes } = useStyles()
  const intl = useIntl()
  const validSearchQuery = getValidSearchQuery({
    focus: router.query.focus,
  })
  const matchKeyToDisplayName = useCallback(
    (selectedFacetKey: string, facets: Facet[] | undefined) => {
      if (
        !facets?.length &&
        validSearchQuery.focus === SearchFocusType.Chromatograms &&
        selectedFacetKey.toUpperCase() === 'FACET_WEB_TITERTEST_APP'
      ) {
        return intl.formatMessage(messages[selectedFacetKey.toUpperCase()])
      }

      if (!facets) return ''
      const facetMatch = facets.find((facet) => facet.key === selectedFacetKey)
      return facetMatch?.key && messages[facetMatch.key.toUpperCase()]
        ? intl.formatMessage(messages[facetMatch.key.toUpperCase()])
        : formatFacetName(facetMatch?.key)
    },
    [intl]
  )

  const facetDisplayName = matchKeyToDisplayName(facetKey, facets)

  return (
    <>
      <HandleMarkup
        className={classes.facetMarkup}
        value={`${facetDisplayName}:`}
      />
      <HandleMarkup value={facetLabel} />
    </>
  )
}

const removeFacetActions = (facet, queryActions): void => {
  queryActions.handleSelectedFacetChange({
    key: facet.key,
    options: [facet.value],
    action: FacetChangeAction.Remove,
  })

  const facetElementText = `${facet.key}:${facet.label}`

  const facetRibbonPayload = buildFacetEventPayload({
    action: 'remove filter',
    component: 'filter ribbon',
    element_type: 'filter ribbon box',
    element_text: facetElementText?.toLowerCase(),
    filter_name: facet.label,
    filter_name_count: facet.count,
    filter_category: facet.key,
  })

  sendEvent({ payload: facetRibbonPayload })
}

interface FacetSelectionsProps {
  facets?: Facet[]
  isStructureSearch: boolean
}

const FacetSelections: React.FC<FacetSelectionsProps> = ({
  facets,
  isStructureSearch,
}) => {
  const [query, selectedFacets, queryActions] = useSearchQuery()
  const { isLargeDesktop, isDesktop } = useResponsive()
  const allOptions = flatMap(facets, 'options')

  const maxCharacters = isLargeDesktop ? 40 : isDesktop ? 23 : 0

  const mappedSelectedFacets = flatMap(selectedFacets, (facet) =>
    facet.options.map((option) => {
      const optionData = allOptions.find((aOpt) => aOpt.value === option)
      return {
        key: facet.key,
        label:
          maxCharacters && option?.length > maxCharacters
            ? `${option.slice(0, maxCharacters)}...`
            : option,
        count: optionData?.count,
        value: option,
      }
    })
  )

  const { formatMessage } = useIntl()
  const router = useRouter()
  const { classes } = useStyles()
  const isKeywordChipShown =
    query.term && !isStructureSearch && !isTermlessSearch(router)

  if (mappedSelectedFacets.length === 0 && !isKeywordChipShown) return null

  return (
    <div className={classes.facetsMenuWrapper} data-testid="facet-selections">
      <Typography variant="caption" className={classes.facetsMenuLabel}>
        <FormattedMessage
          id="APPLIED_FILTERS_HEADING"
          defaultMessage="Applied Filters:"
        />
      </Typography>

      <div className={classes.chipsContainer}>
        {isKeywordChipShown && (
          <div className={classes.keywordChipWrapper}>
            <SelectionChipSrpRedesign
              ariaLabel={`${formatMessage(
                messages.SELECTION_CHIP_REMOVE_FILTER_1
              )} ${query.term} ${formatMessage(
                messages.SELECTION_CHIP_REMOVE_TO_PERFORM_FACET_SEARCH
              )}`}
              label={
                <>
                  <span className={classes.searchTerm}>
                    {formatMessage(messages.KEYWORD)}:
                  </span>
                  <span>{`'${query.term}'`}</span>
                </>
              }
              dataTestId={`query-term-chip-${query.term}`}
              onDelete={() =>
                queryActions.handleTermRemoval(selectedFacets, facets)
              }
            />
          </div>
        )}

        {facets &&
          mappedSelectedFacets.map((facet) => {
            return (
              <div
                className={classes.chipWrapper}
                key={`${facet.key}:${facet.label}`}
              >
                <SelectionChipSrpRedesign
                  ariaLabel={`${formatMessage(
                    messages.SELECTION_CHIP_REMOVE_FILTER_1
                  )} ${facet.label} ${formatMessage(
                    messages.SELECTION_CHIP_REMOVE_FILTER_2
                  )}`}
                  dataTestId={`facet-selection-chip-${facet.key}-${facet.label}`}
                  onDelete={() => removeFacetActions(facet, queryActions)}
                  label={
                    <ChipLabel
                      facetLabel={facet.label}
                      facetKey={facet.key}
                      facets={facets}
                    />
                  }
                />
              </div>
            )
          })}
      </div>
    </div>
  )
}

export default FacetSelections
